import { lightColorPalette } from '@spotnana/blocks/src/BlocksThemeProvider/ux-specs/color';
import { Icon } from '@spotnana/blocks/src/Icon';
import { Typography } from '@spotnana/blocks/src/Typography';
import { margins } from '@spotnana/blocks/src/utils';
import { useBlocksTheme } from '@spotnana/blocks/src/utils/styling/useBlocksTheme';
import { paddings } from '@spotnana/blocks/src/utils/styling/utilityTokens';
import Box from '@spotnana/pixel-react/dist/Box';
import Ellipsis from '@spotnana/pixel-react/dist/Ellipsis';
import Grid from '@spotnana/pixel-react/dist/Grid';
import Image from '@spotnana/pixel-react/dist/Image';
import Tooltip from '@spotnana/pixel-react/dist/Tooltip';
import spotnanaTheme from '@spotnana/pixel-react/dist/utils/themes/theme';
import { useFeatureFlag } from 'obt-common';
import * as React from 'react';
import { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CardBaseContainer, CardDivider, CardTitle, CustomLogoContainer } from './CardBase.styles';

interface CardBaseProps {
  title: string;
  number: string;
  expiryDate: string;
  logo: string;
  company: string;
  onClick: () => void;
  isSelected: boolean;
  isDisabled: boolean;
  customLogo?: string;
  isCentralCard?: boolean;
  hasError?: boolean;
  disableTooltipText?: string;
  cardType?: string;
}

function CardBase({
  expiryDate,
  number,
  title,
  logo,
  company,
  customLogo,
  onClick,
  isSelected,
  isDisabled,
  isCentralCard,
  hasError,
  disableTooltipText,
  cardType,
}: CardBaseProps) {
  const { t: tt } = useTranslation('WEB');
  const isCardOwnershipLabelEnabled = useFeatureFlag('FE_PAYMENTS_CARD_OWNERSHIP_LABEL');
  const { palette } = useBlocksTheme();

  const showCardType = isCardOwnershipLabelEnabled && cardType;

  let cardBaseContainerProps: Record<string, string | boolean> = {
    border: spotnanaTheme.borders.thinSolid,
    bg: 'linear-gradient(to left, #f7f9fb, rgba(247, 249, 251, 0.4))',
    borderColor: spotnanaTheme.colors.border.regular,
    cursor: 'pointer',
  };

  if (isSelected) {
    cardBaseContainerProps = {
      ...cardBaseContainerProps,
      border: spotnanaTheme.borders.thickSolid,
      bg: 'bg.white',
      borderColor: spotnanaTheme.colors.border.greyBlue,
    };
  }

  if (isDisabled) {
    cardBaseContainerProps = {
      ...cardBaseContainerProps,
      opacity: '0.5',
      cursor: 'not-allowed',
      disabled: true,
    };
  }

  if (hasError) {
    cardBaseContainerProps = {
      ...cardBaseContainerProps,
      borderColor: lightColorPalette.error,
      bg: 'bg.white',
    };
  }

  const renderCustomLogo = (): React.ReactNode => {
    if (customLogo) {
      return <img src={customLogo} alt={tt('Custom logo')} width="40px" />;
    }
    if (isCentralCard) {
      return (
        <Icon
          name="OfficeBuilding"
          width={16}
          height={16}
          color={isSelected ? palette.icon.primary : palette.icon.secondary}
        />
      );
    }
    return null;
  };

  const lastFourDigitsText = tt('Last 4 digits of card: {{number}}', { number: number.slice(-4) });
  let cardLabel = `${company}, ${cardType}, ${title}, ${lastFourDigitsText}`;
  if (isSelected) {
    cardLabel = `${cardLabel}, ${tt('Selected')}`;
  } else if (isDisabled) {
    cardLabel = `${cardLabel}, ${tt('Disabled')}`;
  }

  return (
    <CardBaseContainer
      {...cardBaseContainerProps}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
          onClick();
        }
      }}
      aria-label={cardLabel}
      aria-disabled={isDisabled ? true : undefined}
      data-testid="payment-card-item"
    >
      <Tooltip title={disableTooltipText ?? ''} variation="light" placement="top">
        <Box position="relative">
          <div css={showCardType ? [margins.yb.half] : [margins.yb.m1point5]}>
            <CardTitle data-testid="payment-card-title">
              <Ellipsis title={title}>{title}</Ellipsis>
              {showCardType && (
                <Typography
                  variant="body3"
                  color="tertiary"
                  data-testid="card-type-label"
                  css={[margins.yb.m0, paddings.yb.none]}
                >
                  {cardType}
                </Typography>
              )}
            </CardTitle>
          </div>

          <CustomLogoContainer>{renderCustomLogo()}</CustomLogoContainer>

          <CardDivider />
          <Grid.Row between="sm">
            <Grid.Col xs={9}>
              <Typography variant="body3" data-testid="card-number">
                {number}
              </Typography>
              <Typography
                variant="body3"
                data-testid="card-expiry"
                aria-label={tt('Expiry date: {{expiryDate}}', { expiryDate })}
              >
                {expiryDate}
              </Typography>
            </Grid.Col>
            {!!logo && (
              <Grid.Col xs={3}>
                <Image
                  fallback={<Typography variant="body3">{company}</Typography>}
                  src={logo}
                  width="40px"
                  alt={company}
                  loaderType="rectangle"
                  data-testid="payment-card-company"
                />
              </Grid.Col>
            )}
          </Grid.Row>
        </Box>
      </Tooltip>
    </CardBaseContainer>
  );
}

export default CardBase;
